import { useMemo, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { countryName } from '@/i18n';
import { formatDate } from '@/lib/format';
import { useSession } from '@/api/session';
import {
  useDeleteVatClaim,
  useSaveVatClaim,
  useVatClaims,
  useVatSummary,
  type VatClaim,
  type VatClaimStatus,
} from '@/api/hooks/vat';
import { Badge, type Tone } from '@/components/ui/Badge';
import { Button, IconButton } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { DatePicker, Field, Input, Select, Textarea } from '@/components/ui/Field';
import { EmptyState, ErrorState, LoadingBlock } from '@/components/ui/State';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/Table';
import { toast } from '@/stores/toast';

const STATUS: Record<VatClaimStatus, { label: string; tone: Tone }> = {
  draft: { label: 'чернова', tone: 'neutral' },
  submitted: { label: 'подадено', tone: 'info' },
  partially_refunded: { label: 'частично върнато', tone: 'warn' },
  refunded: { label: 'върнато', tone: 'ok' },
  rejected: { label: 'отказано', tone: 'danger' },
};

/** Държавите, в които буса зарежда и плаща такси по маршрутите на фирмата. */
const EU = ['AT', 'BE', 'CZ', 'DE', 'DK', 'ES', 'FR', 'GR', 'HR', 'HU', 'IT', 'LU', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK'];

const eur = (n: number, ccy = 'EUR') =>
  new Intl.NumberFormat('bg-BG', { style: 'currency', currency: ccy, maximumFractionDigits: 2 }).format(n);

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  ДДС ОТ ЧУЖБИНА — колко е възстановено и колко остава
 *
 *  Посредникът подава исканията и връща парите; тук се вписва какво е заявил
 *  и какво е върнато. Възстановимото идва само — от чуждия ДДС в разходите
 *  (горивото от AS 24, таксите, ремонтите). Срокът за една година е 30
 *  септември на следващата; незаявеното след това се губи.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function VatPage() {
  const role = useSession((st) => st.user?.role);
  const now = new Date();
  const years = useMemo(() => [0, 1, 2, 3].map((i) => now.getFullYear() - i), [now]);
  /** До 30 септември основната година е миналата — нейните искания изтичат сега. */
  const [year, setYear] = useState(now.getMonth() < 9 ? now.getFullYear() - 1 : now.getFullYear());
  const summary = useVatSummary(year);
  const claims = useVatClaims(year);
  const remove = useDeleteVatClaim();
  const [editing, setEditing] = useState<VatClaim | 'new' | null>(null);

  if (role !== 'owner' && role !== 'accountant') return <EmptyState title="За управителя и счетоводството." />;
  if (summary.isLoading) return <LoadingBlock />;
  if (summary.isError || !summary.data) return <ErrorState onRetry={() => void summary.refetch()} />;
  const s = summary.data;
  const refundedPct = s.totals.claimable > 0 ? Math.min(100, Math.round((s.totals.refunded * 100) / s.totals.claimable)) : 0;
  const claimedPct = s.totals.claimable > 0 ? Math.min(100, Math.round((s.totals.claimed * 100) / s.totals.claimable)) : 0;
  const urgent = !s.expired && s.daysToDeadline <= 60 && s.totals.unclaimed > 0;

  return (
    <div className="space-y-3 p-3 sm:p-5">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight">ДДС от чужбина</h1>
          <p className="text-sm text-ink-dim">Колко е възстановено и колко остава — по държави, за година.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Select
            className="w-32"
            aria-label="Година"
            value={String(year)}
            onChange={(e) => setYear(Number(e.target.value))}
            options={years.map((y) => ({ value: String(y), label: `за ${y} г.` }))}
          />
          <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setEditing('new')}>
            Искане
          </Button>
        </div>
      </div>

      <div
        className={cn(
          'rounded-lg border px-3 py-2 text-sm',
          s.expired ? 'border-line bg-raised text-ink-dim' : urgent ? 'border-danger/50 bg-danger-soft text-danger' : 'border-line bg-surface',
        )}
      >
        {s.expired
          ? `Срокът за ${year} г. изтече на ${formatDate(s.deadline)}. Незаявеното вече не може да се поиска.`
          : `Срок за исканията за ${year} г.: ${formatDate(s.deadline)} — остават ${s.daysToDeadline} дни.${
              s.totals.unclaimed > 0 ? ` Незаявени: ${eur(s.totals.unclaimed)}.` : ''
            }`}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Tile label="Възстановимо" value={eur(s.totals.claimable)} hint="чужд ДДС в разходите" />
        <Tile label="Заявено" value={eur(s.totals.claimed)} hint={`${claimedPct} % от възстановимото`} />
        <Tile label="Възстановено" value={eur(s.totals.refunded)} hint={s.totals.fee ? `нето ${eur(s.totals.net)} след таксата` : 'върнато от държавите'} accent />
        <Tile label="Чака се" value={eur(s.totals.outstanding)} hint="заявено, още невърнато" />
        <Tile label="Незаявено" value={eur(s.totals.unclaimed)} hint="може да се поиска до срока" warn={s.totals.unclaimed > 0 && !s.expired} />
      </div>

      <Card className="p-3">
        <div className="mb-1 flex justify-between text-xs text-ink-dim">
          <span>Възстановено от възстановимото</span>
          <span className="font-mono tabular">{refundedPct} %</span>
        </div>
        <div className="relative h-2.5 overflow-hidden rounded-full bg-raised">
          <div className="absolute inset-y-0 left-0 rounded-full bg-accent/30" style={{ width: `${claimedPct}%` }} />
          <div className="absolute inset-y-0 left-0 rounded-full bg-ok" style={{ width: `${refundedPct}%` }} />
        </div>
        <div className="mt-1 flex gap-4 text-[11px] text-ink-faint">
          <span>
            <span className="mr-1 inline-block size-2 rounded-full bg-ok" />
            възстановено
          </span>
          <span>
            <span className="mr-1 inline-block size-2 rounded-full bg-accent/30" />
            заявено
          </span>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader title="По държави" subtitle="Възстановимото е от разходите с чужд ДДС; заявеното и върнатото — от исканията по-долу." />
        {s.countries.length === 0 ? (
          <EmptyState title="Няма разходи с чужд ДДС за тази година." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <THead>
                <TR>
                  <TH>Държава</TH>
                  <TH numeric>Възстановимо</TH>
                  <TH numeric>Заявено</TH>
                  <TH numeric>Възстановено</TH>
                  <TH numeric>Чака се</TH>
                  <TH numeric>Незаявено</TH>
                  <TH>Бележки</TH>
                </TR>
              </THead>
              <TBody>
                {s.countries.map((c) => (
                  <TR key={c.country}>
                    <TD className="font-medium">{countryName(c.country)}</TD>
                    <TD numeric>{eur(c.claimable)}</TD>
                    <TD numeric>{c.claimed ? eur(c.claimed) : '—'}</TD>
                    <TD numeric className={c.refunded ? 'text-ok' : undefined}>{c.refunded ? eur(c.refunded) : '—'}</TD>
                    <TD numeric>{c.outstanding ? eur(c.outstanding) : '—'}</TD>
                    <TD numeric className={c.unclaimed && !s.expired ? 'text-warn' : undefined}>{c.unclaimed ? eur(c.unclaimed) : '—'}</TD>
                    <TD className="text-xs text-ink-dim">
                      {[
                        !c.meetsAnnualMinimum && c.claimable > 0 ? 'под 50 € — не се заявява' : null,
                        c.missingDocuments ? `${c.missingDocuments} разхода без документ` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || ''}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        )}
      </Card>

      <Card className="overflow-hidden">
        <CardHeader title="Искания" subtitle="Вписват се по справката от посредника — заявено, върнато, отказано, такса." />
        {(claims.data ?? []).length === 0 ? (
          <EmptyState title="Още няма вписани искания за тази година." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <THead>
                <TR>
                  <TH>Държава</TH>
                  <TH>Период</TH>
                  <TH>Посредник / №</TH>
                  <TH numeric>Заявено</TH>
                  <TH numeric>Върнато</TH>
                  <TH numeric>Чака се</TH>
                  <TH>Състояние</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>
                {(claims.data ?? []).map((c) => (
                  <TR key={c.id}>
                    <TD className="font-medium">{countryName(c.country)}</TD>
                    <TD className="whitespace-nowrap text-xs">
                      {c.periodFrom && c.periodTo ? `${formatDate(c.periodFrom)} – ${formatDate(c.periodTo)}` : `${c.periodYear} г.`}
                    </TD>
                    <TD className="text-xs text-ink-dim">
                      {c.agent ?? '—'}
                      {c.reference ? <span className="block font-mono">{c.reference}</span> : null}
                    </TD>
                    <TD numeric>{eur(c.claimed, c.ccy)}</TD>
                    <TD numeric>
                      {c.refunded ? eur(c.refunded, c.ccy) : '—'}
                      {c.refundedOn ? <span className="block text-[10px] text-ink-faint">{formatDate(c.refundedOn)}</span> : null}
                    </TD>
                    <TD numeric>{c.outstanding ? eur(c.outstanding, c.ccy) : '—'}</TD>
                    <TD>
                      <Badge tone={STATUS[c.status].tone}>{STATUS[c.status].label}</Badge>
                    </TD>
                    <TD className="whitespace-nowrap text-right">
                      <IconButton label="Редактирай" onClick={() => setEditing(c)}>
                        <Pencil className="size-3.5" />
                      </IconButton>
                      <IconButton
                        label="Изтрий"
                        onClick={() => window.confirm('Да изтрия ли това искане?') && remove.mutate(c.id)}
                      >
                        <Trash2 className="size-3.5" />
                      </IconButton>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        )}
        {s.otherCurrencyClaims ? (
          <p className="border-t border-line px-3 py-2 text-[11px] text-ink-faint">
            {s.otherCurrencyClaims} искания в друга валута не са в сборовете в евро.
          </p>
        ) : null}
      </Card>

      {editing ? (
        <ClaimDialog
          year={year}
          claim={editing === 'new' ? null : editing}
          lastAgent={(claims.data ?? []).find((c) => c.agent)?.agent ?? ''}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

function Tile({ label, value, hint, accent, warn }: { label: string; value: string; hint?: string; accent?: boolean; warn?: boolean }) {
  return (
    <Card className={cn('p-3', accent && 'border-accent/50', warn && 'border-warn/50')}>
      <div className="text-[11px] uppercase tracking-wide text-ink-faint">{label}</div>
      <div className={cn('mt-0.5 font-mono text-lg font-semibold tabular', warn && 'text-warn')}>{value}</div>
      {hint ? <div className="text-[11px] text-ink-dim">{hint}</div> : null}
    </Card>
  );
}

function num(raw: string): number {
  const value = Number(raw.replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(value) && value >= 0 ? value : 0;
}

function ClaimDialog({ year, claim, lastAgent, onClose }: { year: number; claim: VatClaim | null; lastAgent: string; onClose: () => void }) {
  const save = useSaveVatClaim();
  const [form, setForm] = useState({
    agent: claim?.agent ?? lastAgent,
    country: claim?.country ?? 'HU',
    periodYear: String(claim?.periodYear ?? year),
    periodFrom: claim?.periodFrom ?? '',
    periodTo: claim?.periodTo ?? '',
    claimed: claim ? String(claim.claimed).replace('.', ',') : '',
    ccy: claim?.ccy ?? 'EUR',
    submittedOn: claim?.submittedOn ?? '',
    reference: claim?.reference ?? '',
    status: claim?.status ?? 'submitted',
    refunded: claim ? String(claim.refunded).replace('.', ',') : '0',
    refundedOn: claim?.refundedOn ?? '',
    rejected: claim ? String(claim.rejected).replace('.', ',') : '0',
    agentFee: claim ? String(claim.agentFee).replace('.', ',') : '0',
    notes: claim?.notes ?? '',
  });
  const set = (key: keyof typeof form, value: string) => setForm({ ...form, [key]: value });

  const submit = () =>
    save.mutate(
      {
        ...(claim ? { id: claim.id } : {}),
        body: {
          agent: form.agent.trim() || null,
          country: form.country,
          periodYear: Number(form.periodYear),
          periodFrom: form.periodFrom || null,
          periodTo: form.periodTo || null,
          claimed: num(form.claimed),
          ccy: form.ccy,
          submittedOn: form.submittedOn || null,
          reference: form.reference.trim() || null,
          status: form.status as VatClaimStatus,
          refunded: num(form.refunded),
          refundedOn: form.refundedOn || null,
          rejected: num(form.rejected),
          agentFee: num(form.agentFee),
          notes: form.notes.trim() || null,
        },
      },
      {
        onSuccess: () => {
          toast.ok('Искането е записано');
          onClose();
        },
        onError: (error) => toast.error('Искането не е записано', error instanceof Error ? error.message : undefined),
      },
    );

  return (
    <Dialog
      open
      onClose={onClose}
      width="lg"
      title={claim ? 'Искане за ДДС' : 'Ново искане за ДДС'}
      description="Попълва се по справката от посредника. Състоянието се сменя само според сумите."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отказ
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={!form.claimed || num(form.claimed) <= 0} onClick={submit}>
            Запиши
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Държава">
          <Select value={form.country} onChange={(e) => set('country', e.target.value)} options={EU.map((c) => ({ value: c, label: countryName(c) }))} />
        </Field>
        <Field label="За година">
          <Input inputMode="numeric" value={form.periodYear} onChange={(e) => set('periodYear', e.target.value)} />
        </Field>
        <Field label="Посредник">
          <Input value={form.agent} onChange={(e) => set('agent', e.target.value)} placeholder="фирмата, която подава" />
        </Field>
        <Field label="Период от (ако е част от годината)">
          <DatePicker withTime={false} value={form.periodFrom} onChange={(e) => set('periodFrom', e.target.value)} />
        </Field>
        <Field label="Период до">
          <DatePicker withTime={false} value={form.periodTo} onChange={(e) => set('periodTo', e.target.value)} />
        </Field>
        <Field label="№ на искането">
          <Input value={form.reference} onChange={(e) => set('reference', e.target.value)} />
        </Field>
        <Field label="Заявено" required>
          <Input inputMode="decimal" value={form.claimed} onChange={(e) => set('claimed', e.target.value)} placeholder="0,00" />
        </Field>
        <Field label="Валута">
          <Select value={form.ccy} onChange={(e) => set('ccy', e.target.value)} options={['EUR', 'HUF', 'RON', 'CZK', 'PLN', 'BGN'].map((c) => ({ value: c, label: c }))} />
        </Field>
        <Field label="Подадено на">
          <DatePicker withTime={false} value={form.submittedOn} onChange={(e) => set('submittedOn', e.target.value)} />
        </Field>
        <Field label="Върнато">
          <Input inputMode="decimal" value={form.refunded} onChange={(e) => set('refunded', e.target.value)} />
        </Field>
        <Field label="Върнато на">
          <DatePicker withTime={false} value={form.refundedOn} onChange={(e) => set('refundedOn', e.target.value)} />
        </Field>
        <Field label="Отказано">
          <Input inputMode="decimal" value={form.rejected} onChange={(e) => set('rejected', e.target.value)} />
        </Field>
        <Field label="Такса на посредника">
          <Input inputMode="decimal" value={form.agentFee} onChange={(e) => set('agentFee', e.target.value)} />
        </Field>
        <Field label="Състояние" hint="„Чернова“ и „отказано“ се пазят; останалото следва сумите.">
          <Select
            value={form.status}
            onChange={(e) => set('status', e.target.value)}
            options={(Object.keys(STATUS) as VatClaimStatus[]).map((k) => ({ value: k, label: STATUS[k].label }))}
          />
        </Field>
        <Field label="Бележка" className="sm:col-span-3">
          <Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
